import './ContactForm.scss';
import React, { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { ContactTier, TIER_LABELS, TIER_DESCRIPTIONS } from '../../types/entity';

interface ContactFormProps {
  windowId: string;
  spaceId: string;
  contactId?: string;
  onClose: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  tier: ContactTier;
  notes: string;
  tags: string;
  linkedinUrl: string;
  twitterHandle: string;
  githubHandle: string;
}

const DEFAULT_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  title: '',
  tier: 'tier3',
  notes: '',
  tags: '',
  linkedinUrl: '',
  twitterHandle: '',
  githubHandle: '',
};

const TIERS: ContactTier[] = ['inner_circle', 'tier1', 'tier2', 'tier3', 'dormant'];

export const ContactForm: React.FC<ContactFormProps> = ({ windowId, spaceId, contactId, onClose }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const contacts     = store((s) => s.contacts);
  const createContact = store((s) => s.createContact);
  const updateContact = store((s) => s.updateContact);
  const selectContact = store((s) => s.selectContact);

  const isEditing = Boolean(contactId);
  const existing  = contactId ? contacts.find((c) => c._id === contactId) : undefined;

  const [form, setForm]       = useState<FormState>(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (existing) {
      const sk = existing.skeleton;
      setForm({
        name:           existing.name,
        email:          sk.email          || '',
        phone:          sk.phone          || '',
        company:        sk.company        || '',
        title:          sk.title          || '',
        tier:           sk.tier,
        notes:          sk.notes          || '',
        tags:           (sk.tags || []).join(', '),
        linkedinUrl:    sk.linkedinUrl    || '',
        twitterHandle:  sk.twitterHandle  || '',
        githubHandle:   sk.githubHandle   || '',
      });
    }
  }, [contactId]);

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    const tags = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      if (isEditing && contactId) {
        await updateContact(contactId, {
          name:          form.name.trim(),
          email:         form.email    || undefined,
          phone:         form.phone    || undefined,
          company:       form.company  || undefined,
          title:         form.title    || undefined,
          tier:          form.tier,
          notes:         form.notes    || undefined,
          tags,
          linkedinUrl:   form.linkedinUrl   || undefined,
          twitterHandle: form.twitterHandle || undefined,
          githubHandle:  form.githubHandle  || undefined,
        });
        onClose();
      } else {
        const created = await createContact({
          name:    form.name.trim(),
          email:   form.email   || undefined,
          phone:   form.phone   || undefined,
          company: form.company || undefined,
          tier:    form.tier,
          notes:   form.notes   || undefined,
        });
        if (created) {
          selectContact(created._id);
        }
        onClose();
      }
    } catch {
      setError('Failed to save contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="contact-form-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="contact-form">
        <div className="contact-form__header">
          <h2>{isEditing ? 'Edit Contact' : 'New Contact'}</h2>
          <button className="contact-form__close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form className="contact-form__body" onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className="contact-form__section">
            <div className="contact-form__field">
              <label>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={set('name')}
                placeholder="Full name"
                autoFocus
              />
            </div>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label>Email</label>
                <input type="email" value={form.email} onChange={set('email')} placeholder="email@example.com" />
              </div>
              <div className="contact-form__field">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label>Company</label>
                <input type="text" value={form.company} onChange={set('company')} placeholder="Company or org" />
              </div>
              <div className="contact-form__field">
                <label>Title</label>
                <input type="text" value={form.title} onChange={set('title')} placeholder="Job title" />
              </div>
            </div>
          </div>

          {/* Relationship tier */}
          <div className="contact-form__section">
            <label className="contact-form__section-label">Relationship Tier</label>
            <div className="contact-form__tiers">
              {TIERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`contact-form__tier-option${form.tier === t ? ' contact-form__tier-option--active' : ''} contact-form__tier-option--${t}`}
                  onClick={() => setForm((f) => ({ ...f, tier: t }))}
                >
                  <span className="contact-form__tier-name">{TIER_LABELS[t]}</span>
                  <span className="contact-form__tier-desc">{TIER_DESCRIPTIONS[t]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Socials */}
          <div className="contact-form__section">
            <label className="contact-form__section-label">Socials</label>
            <div className="contact-form__field">
              <label>LinkedIn URL</label>
              <input type="url" value={form.linkedinUrl} onChange={set('linkedinUrl')} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label>Twitter / X</label>
                <input type="text" value={form.twitterHandle} onChange={set('twitterHandle')} placeholder="handle (no @)" />
              </div>
              <div className="contact-form__field">
                <label>GitHub</label>
                <input type="text" value={form.githubHandle} onChange={set('githubHandle')} placeholder="username" />
              </div>
            </div>
          </div>

          {/* Tags & notes */}
          <div className="contact-form__section">
            <div className="contact-form__field">
              <label>Tags</label>
              <input
                type="text"
                value={form.tags}
                onChange={set('tags')}
                placeholder="investor, advisor, client (comma-separated)"
              />
            </div>
            <div className="contact-form__field">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={set('notes')}
                placeholder="Context, background, communication style…"
                rows={3}
              />
            </div>
          </div>

          {error && <p className="contact-form__error">{error}</p>}

          <div className="contact-form__footer">
            <button type="button" className="contact-form__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="contact-form__btn contact-form__btn--primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
